import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  serverTimestamp,
  doc,
  updateDoc
} from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import config from '../config/environment';

class NotificationService {
  constructor() {
    this.messaging = null;
    this.notifications = [];
    this.listeners = [];
    this.init();
  }

  async init() {
    try {
      // Initialize Firebase messaging
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        this.messaging = getMessaging();
        
        // Request notification permission
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          await this.getFCMToken();
        }
      }
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
    }
  }

  async getFCMToken() {
    try {
      if (!this.messaging) return null;

      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || config?.firebase?.vapidKey;
      if (!vapidKey || vapidKey === 'YOUR_VAPID_KEY') {
        // Avoid noisy errors in production if push isn't configured yet
        console.warn('FCM VAPID key not configured. Skipping push token registration.');
        return null;
      }

      const token = await getToken(this.messaging, {
        vapidKey
      });

      if (token) {
        localStorage.setItem('fcm-token', token);
        return token;
      }
    } catch (error) {
      console.error('Failed to get FCM token:', error);
    }
    return null;
  }

  // Send notification to customer when car is ready for collection
  async sendCarReadyNotification(washId, customerId, customerName, carDetails) {
    try {
      const notification = {
        type: 'car_ready',
        title: '🚗 Your Car is Ready for Collection!',
        message: `Your ${carDetails.make} ${carDetails.model} is ready for collection.`,
        washId,
        customerId,
        customerName,
        carDetails,
        timestamp: serverTimestamp(),
        read: false,
        priority: 'high'
      };

      await addDoc(collection(db, 'notifications'), notification);
      
      // Also send push notification if possible
      await this.sendPushNotification(notification);
      
      return notification;
    } catch (error) {
      console.error('Failed to send car ready notification:', error);
      throw error;
    }
  }

  // Send notification to staff when new wash is assigned
  async sendWashAssignedNotification(washId, staffId, customerName, carDetails) {
    try {
      const notification = {
        type: 'wash_assigned',
        title: '🧽 New Wash Assigned',
        message: `New wash assigned: ${carDetails.make} ${carDetails.model} for ${customerName}`,
        washId,
        staffId,
        customerName,
        carDetails,
        timestamp: serverTimestamp(),
        read: false,
        priority: 'medium'
      };

      await addDoc(collection(db, 'notifications'), notification);
      return notification;
    } catch (error) {
      console.error('Failed to send wash assigned notification:', error);
      throw error;
    }
  }

  // Send notification for wash completion
  async sendWashCompletedNotification(washId, customerId, customerName, carDetails) {
    try {
      const notification = {
        type: 'wash_completed',
        title: '✨ Wash Completed',
        message: `Your ${carDetails.make} ${carDetails.model} wash has been completed successfully.`,
        washId,
        customerId,
        customerName,
        carDetails,
        timestamp: serverTimestamp(),
        read: false,
        priority: 'high'
      };

      await addDoc(collection(db, 'notifications'), notification);
      await this.sendPushNotification(notification);
      return notification;
    } catch (error) {
      console.error('Failed to send wash completed notification:', error);
      throw error;
    }
  }

  // Send push notification using browser API
  async sendPushNotification(notification) {
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.ready;
        
        await registration.showNotification(notification.title, {
          body: notification.message,
          icon: '/icon-192.png', // Updated to use existing icon
          badge: '/favicon-32x32.png', // Updated to use existing icon
          tag: notification.washId,
          requireInteraction: true,
          actions: [
            {
              action: 'view',
              title: 'View Details'
            },
            {
              action: 'dismiss',
              title: 'Dismiss'
            }
          ],
          data: {
            washId: notification.washId,
            type: notification.type
          }
        });
      }
    } catch (error) {
      console.error('Failed to send push notification:', error);
    }
  }

  // Listen to notifications for a specific user
  listenToUserNotifications(userId, callback) {
    const q = query(
      collection(db, 'notifications'),
      where('customerId', '==', userId),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications = [];
      snapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      this.notifications = notifications;
      callback(notifications);
    });

    this.listeners.push(unsubscribe);
    return unsubscribe;
  }

  // Listen to staff notifications
  listenToStaffNotifications(staffId, callback) {
    const q = query(
      collection(db, 'notifications'),
      where('staffId', '==', staffId),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications = [];
      snapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      callback(notifications);
    });

    this.listeners.push(unsubscribe);
    return unsubscribe;
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }

  // Mark all notifications as read for a user
  async markAllAsRead(userId) {
    try {
      const userNotifications = this.notifications.filter(n => n.customerId === userId && !n.read);
      
      const updatePromises = userNotifications.map(notification => 
        updateDoc(doc(db, 'notifications', notification.id), {
          read: true
        })
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  }

  // Get unread notification count
  getUnreadCount(userId) {
    return this.notifications.filter(n => n.customerId === userId && !n.read).length;
  }

  // Cleanup listeners
  cleanup() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners = [];
  }

  // Handle notification click
  handleNotificationClick(notification) {
    // Navigate to appropriate page based on notification type
    switch (notification.type) {
      case 'car_ready':
      case 'wash_completed':
        // Navigate to customer dashboard or wash details
        window.location.href = `/customer?washId=${notification.washId}`;
        break;
      case 'wash_assigned':
        // Navigate to wash queue for staff
        window.location.href = `/wash-queue?washId=${notification.washId}`;
        break;
      default:
        break;
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService; 